<script setup lang="ts">
  import { computed, ref } from "vue";

  type Props = {
    open: boolean;
    title?: string;
    subtitle?: string;
  };

  const props = defineProps<Props>();
  const emit = defineEmits<{ "update:open": [value: boolean] }>();

  // Past this downward distance (px) the sheet is dismissed on release.
  const CLOSE_THRESHOLD = 80;
  // Movement (px) before a touch is treated as a swipe rather than a tap.
  const DRAG_START_THRESHOLD = 6;

  const panelRef = ref<HTMLElement | null>(null);
  const dragging = ref(false);
  const startY = ref(0);
  const dragY = ref(0);
  // The pointer is down and we're still deciding whether it's a dismiss swipe.
  let evaluating = false;
  let activePointerId: number | null = null;
  // The scroll container the gesture started in (if any), so we let it scroll
  // instead of dismissing until it reaches the top.
  let scrollEl: HTMLElement | null = null;

  const panelStyle = computed(() => ({
    transform: dragY.value ? `translateY(${dragY.value}px)` : undefined,
    transition: dragging.value ? "none" : undefined,
  }));

  function close() {
    emit("update:open", false);
  }

  // Nearest scrollable ancestor of `start` within the panel, or null when the
  // gesture began on a non-scrollable area (handle, header, ...).
  function findScrollable(start: EventTarget | null): HTMLElement | null {
    const panel = panelRef.value;
    let node = start instanceof HTMLElement ? start : null;
    while (node && panel?.contains(node)) {
      const overflowY = getComputedStyle(node).overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function handlePointerDown(event: PointerEvent) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    evaluating = true;
    activePointerId = event.pointerId;
    startY.value = event.clientY;
    dragY.value = 0;
    scrollEl = findScrollable(event.target);
  }

  function handlePointerMove(event: PointerEvent) {
    if (!evaluating || event.pointerId !== activePointerId) return;
    const delta = event.clientY - startY.value;

    if (!dragging.value) {
      // Wait for a clear downward pull before committing to a dismiss.
      if (delta <= DRAG_START_THRESHOLD) return;
      // If the gesture is inside a scroll area that can still scroll up, let it
      // scroll natively and abandon the dismiss for this gesture.
      if (scrollEl && scrollEl.scrollTop > 0) {
        evaluating = false;
        return;
      }
      dragging.value = true;
      panelRef.value?.setPointerCapture(event.pointerId);
    }

    dragY.value = Math.max(0, delta);
    event.preventDefault();
  }

  function handlePointerEnd(event: PointerEvent) {
    if (event.pointerId !== activePointerId) return;
    evaluating = false;
    activePointerId = null;
    scrollEl = null;
    if (!dragging.value) return;

    dragging.value = false;
    if (panelRef.value?.hasPointerCapture(event.pointerId)) {
      panelRef.value.releasePointerCapture(event.pointerId);
    }
    if (dragY.value > CLOSE_THRESHOLD) close();
    dragY.value = 0;
  }
</script>

<template>
  <Teleport to="body">
    <Transition :name="$style.sheet" appear>
      <div v-if="props.open" :class="$style.root" role="dialog" aria-modal="true">
        <div :class="$style.backdrop" @click="close"></div>
        <div
          ref="panelRef"
          :class="$style.panel"
          :style="panelStyle"
          @pointerdown="handlePointerDown"
          @pointermove="handlePointerMove"
          @pointerup="handlePointerEnd"
          @pointercancel="handlePointerEnd"
        >
          <div :class="$style.handleArea">
            <div :class="$style.handle"></div>
          </div>
          <div v-if="props.title || props.subtitle" :class="$style.header">
            <span v-if="props.title" :class="$style.title">{{ props.title }}</span>
            <span v-if="props.subtitle" :class="$style.subtitle">{{ props.subtitle }}</span>
          </div>
          <div :class="$style.content">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" module>
  .root {
    position: fixed;
    inset: 0;
    z-index: 1100;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .backdrop {
    position: absolute;
    inset: 0;

    background: rgb(0 0 0 / 0.5);
  }

  .panel {
    position: relative;
    width: 100%;
    max-height: 90vh;

    display: flex;
    flex-direction: column;

    background-color: var(--ksd-bg-color);
    color: var(--foreground);

    border-top-left-radius: var(--radius);
    border-top-right-radius: var(--radius);
    border-top: 1px solid var(--border-subtle);

    transition: transform 0.25s ease;
    // The panel is a JS-driven drag surface: with `pan-y` the browser claims
    // vertical drags for scrolling and cancels our pointer stream, so the
    // swipe-to-dismiss never starts. `none` hands us the full gesture; the
    // scrollable `.content` below opts back into native vertical scrolling.
    touch-action: none;
  }

  .handleArea {
    width: 100%;
    padding: 12px 0 8px;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .handle {
    width: 100px;
    height: 8px;

    border-radius: 999px;
    background-color: var(--border);
  }

  .header {
    padding: 4px 20px 8px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    text-align: center;
  }

  .title {
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1rem;
    color: var(--foreground);
  }

  .subtitle {
    font-weight: 500;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--muted-foreground);
  }

  .content {
    padding: 8px 20px 24px;
    min-height: 0;
    height: 100%;

    display: flex;
    flex: 1;
  }

  .sheet:global(-enter-active),
  .sheet:global(-leave-active) {
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;

    .panel {
      transition: transform 0.25s ease;
    }

    .backdrop {
      transition: opacity 0.25s ease;
    }
  }

  .sheet:global(-enter-from),
  .sheet:global(-leave-to) {
    .panel {
      transform: translateY(100%);
    }

    .backdrop {
      opacity: 0;
    }
  }
</style>
